import { Injectable } from '@angular/core';
import { Subject, timer, Subscription } from 'rxjs';
import { AppEvent } from 'src/app/models/event.model';

/**
 * EventRefreshService — bus réactif centralisé.
 *
 * Deux streams distincts :
 *  - dataChange$ : mutation réelle (create/delete) → les composants font un reload HTTP
 *  - transitions$ : franchissement d'heure (planned→active, active→completed)
 *                   → les composants reclassifient localement, SANS appel réseau
 *
 * Usage :
 *   trigger()               — après create/delete
 *   scheduleTransitions()   — après chaque chargement d'events
 */
@Injectable({ providedIn: 'root' })
export class EventRefreshService {
  /** Émis après une mutation réelle (create / delete). */
  readonly dataChange$ = new Subject<void>();

  /** Émis quand un event doit changer de statut selon l'heure courante. */
  readonly transitions$ = new Subject<void>();

  private timerSub: Subscription | null = null;

  /** Déclenche un reload HTTP dans les composants abonnés (create/delete). */
  trigger(): void {
    this.dataChange$.next();
  }

  /**
   * Annule le timer précédent et programme le prochain franchissement de statut.
   * Utilise timer() RxJS (propre, annulable via unsubscribe).
   *
   * Transitions détectées :
   *  - planned → active  : à startTime du jour J (ou minuit si absent)
   *  - active  → completed : à endTime du jour J (ou 23:59 si absent)
   *
   * Quand le timer se déclenche → transitions$.next() puis le composant
   * rappelle scheduleTransitions() avec les données fraîches → chaîne auto-renouvelée.
   */
  scheduleTransitions(events: AppEvent[]): void {
    this.cancelTimer();
    const now = Date.now();
    let minMs = Infinity;

    for (const evt of events) {
      // Programmer startTime ET endTime indépendamment du statut DB
      // (le statut local peut diverger du statut DB après reclassification)
      const startMs = this.toTimestamp(evt.eventDate, evt.startTime);
      const endMs = this.toTimestamp(evt.eventDate, evt.endTime || '23:59');

      if (startMs > now && startMs < minMs) minMs = startMs;
      if (endMs > now && endMs < minMs) minMs = endMs;
    }

    if (minMs === Infinity) return;

    // Clamp à la limite JS (~24.8 jours)
    const delay = Math.min(minMs - now, 2_147_483_647);

    this.timerSub = timer(delay).subscribe(() => {
      this.timerSub = null;
      this.transitions$.next();
    });
  }

  cancelTimer(): void {
    this.timerSub?.unsubscribe();
    this.timerSub = null;
  }

  private toTimestamp(date: Date | string, time?: string | null): number {
    const d = new Date(date);
    if (time) {
      const [h, m] = time.split(':').map(Number);
      d.setHours(h, m, 0, 0);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d.getTime();
  }
}
